import React from "react";
import { Loader2, MessageSquare, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Which callout, if any, the Overview shows above its two columns.
 *
 * `fresh` and `awaitingFirstBuild` are separated deliberately. TACO does not
 * generate artifacts on the first turn by design — it researches, proposes a
 * plan, asks what it still needs, and stops. So "nothing has been written yet"
 * is the *expected* state after a first prompt, not a failure, and saying so is
 * the difference between a user waiting confidently and a user thinking the
 * product broke.
 *
 * There is deliberately no `waiting` state for open questions yet: the agent
 * records no structured question data, so any count would be guesswork.
 */
export type ProjectState =
  | "working"
  | "awaitingFirstBuild"
  | "fresh"
  | "resting";

interface ProjectStateCalloutProps {
  state: ProjectState;
  /** Focuses the chat input. */
  onStartInChat: () => void;
}

/** Informational, not a warning — none of these states is a problem. */
const SHELL =
  "flex items-start gap-3.5 border border-border rounded-xl px-4 py-4 mb-5";

const ProjectStateCallout: React.FC<ProjectStateCalloutProps> = ({
  state,
  onStartInChat,
}) => {
  if (state === "working") {
    return (
      <div className={SHELL}>
        <Loader2 className="mt-0.5 h-5 w-5 flex-none animate-spin text-blue-600 dark:text-blue-400" />
        <div>
          <div className="text-sm font-semibold text-foreground">
            TACO is working on this project
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Follow along step by step in the chat panel. Deliverables appear
            here as they are written.
          </div>
        </div>
      </div>
    );
  }

  if (state === "awaitingFirstBuild") {
    return (
      <div className={SHELL}>
        <MessageSquare className="mt-0.5 h-5 w-5 flex-none text-blue-600 dark:text-blue-400" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            TACO is scoping this out before it builds
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            TACO researches the problem and confirms what it needs before
            generating artifacts. Answer its questions in chat to keep going, or
            ask it to make assumptions and build now.
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={onStartInChat}
          >
            Reply in chat
          </Button>
        </div>
      </div>
    );
  }

  if (state === "fresh") {
    return (
      <div className={SHELL}>
        <Sparkles className="mt-0.5 h-5 w-5 flex-none text-blue-600 dark:text-blue-400" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Ready when you are
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Describe what you need in the chat panel — a business problem, a
            customer requirement, an existing architecture, or just the artifact
            you want. TACO will scope it out with you before it starts building.
          </div>
          <Button size="sm" className="mt-3" onClick={onStartInChat}>
            Start in chat
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default ProjectStateCallout;
